'use client'

import { useEffect } from 'react'
import { onAuthStateChanged } from 'firebase/auth'
import { usePathname, useRouter } from 'next/navigation'
import { auth } from '@/firebase/config'
import { saveUserToFirestore } from '@/firebase/utils'
import useAuthStore from '@/store/useAuthStore'
import useOnboardingStore from '@/store/useOnboardingStore'
import LoadingSpinner from '@/components/LoadingSpinner'

// Routes that authenticated users should be redirected away from (to /agenda)
const authRoutes = ['/login', '/signup', '/forgot-password', '/onboarding']

// Public content routes accessible to everyone (authenticated or not)
const publicContentRoutes = ['/', '/v2', '/privacy', '/terms', '/features', '/docs', '/pricing', '/contact', '/tutorials', '/help']

// Combined list of all routes that don't require authentication
const publicRoutes = [...authRoutes, ...publicContentRoutes] 

export default function AuthProvider({ children }) {
  // All hooks at top level
  const router = useRouter()
  const pathname = usePathname()
  const { user, loading, setUser, setLoading, fetchLabels } = useAuthStore()
  const { hasCompletedOnboarding, setOnboardingCompleted } = useOnboardingStore()

  useEffect(() => {
    let isMounted = true;
    setLoading(true)

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      
      // CRITICAL: Check authentication flag FIRST before any processing
      const isAuthenticating = sessionStorage.getItem('isAuthenticating');
      const showOnboarding = sessionStorage.getItem('showOnboarding');
      
      if (isAuthenticating === 'true' || showOnboarding === 'true') {
        if (firebaseUser && isMounted) {
          // Still update user data but don't redirect
          try {
            const userData = await saveUserToFirestore(firebaseUser)
            setUser(userData)
            await fetchLabels(userData.uid)
          } catch (error) {
            console.error('Error updating user data:', error)
          }
        }
        if (isMounted) {
          setLoading(false)
        }
        return; // Exit early - no redirects
      }
      
      try {
        if (firebaseUser && isMounted) {
          // Force token refresh to ensure valid session
          // This is critical for preventing session issues when switching providers
          try {
            await firebaseUser.getIdToken(true)
          } catch (tokenError) {
            console.error('Error refreshing token:', tokenError)
            // If token refresh fails, the session is invalid
            setUser(null)
            router.push('/login')
            return
          }
          
          // Show the app as soon as Firebase Auth knows who this is. The profile
          // document, the last-login write and the labels load in the background.
          setUser({
            uid: firebaseUser.uid,
            email: firebaseUser.email || '',
            displayName: firebaseUser.displayName || '',
            photoURL: firebaseUser.photoURL || '',
            authProviders: firebaseUser.providerData.map((p) => p.providerId),
          })
          saveUserToFirestore(firebaseUser)
            .then((userData) => {
              if (!isMounted) return undefined
              useAuthStore.setState((s) => ({ user: s.user?.uid === userData.uid ? { ...s.user, ...userData } : s.user }))
              return fetchLabels(userData.uid)
            })
            .catch((error) => console.error('Error syncing user profile:', error))
          
          // Don't redirect from login page or onboarding page - let them handle their own flows
          
          // Never redirect from login or onboarding pages
          // Also check if we just came from login (authentication just completed)
          const justAuthenticated = sessionStorage.getItem('justAuthenticated');
          
          if (pathname === '/login') {
            // Don't redirect - let login-form.jsx handle the flow
          } else if (pathname === '/onboarding') {
            // Don't redirect - let onboarding complete
          } else if (justAuthenticated === 'true') {
            // Clear the flag after a delay
            setTimeout(() => {
              sessionStorage.removeItem('justAuthenticated');
            }, 2000);
          } else if (authRoutes.includes(pathname)) {
            // Redirect authenticated users away from auth pages (login, signup, etc.) to agenda
            router.push('/agenda')
          } else if (publicContentRoutes.includes(pathname)) {
            // Allow authenticated users to stay on public content pages (privacy, terms, etc.)
          }
        } else if (isMounted) {
          setUser(null)
          if (!publicRoutes.includes(pathname)) {
            router.push('/login')
          }
        }
      } catch (error) {
        console.error('Error in auth state change:', error)
        if (firebaseUser && isMounted) {
          // Fallback: still set basic user data if Firestore fails
          setUser({
            uid: firebaseUser.uid,
            email: firebaseUser.email || '',
            displayName: firebaseUser.displayName || '',
            photoURL: firebaseUser.photoURL || '',
          })
        }
      } finally {
        if (isMounted) {
          setLoading(false)
        }
      }
    })
  
    return () => {
      isMounted = false;
      unsubscribe()
    }
  }, [])  // Remove pathname dependency

  // Show loading spinner only during initial auth check
  // Never show spinner if we're authenticating or showing onboarding
  const isAuthenticating = typeof window !== 'undefined' ? sessionStorage.getItem('isAuthenticating') : null;
  const showOnboarding = typeof window !== 'undefined' ? sessionStorage.getItem('showOnboarding') : null;
  
  // Only show loading spinner during initial auth check, not during navigation
  // Don't show spinner for public routes - let them render immediately
  const isPublicRoute = publicContentRoutes.includes(pathname)
  if (loading && !isAuthenticating && !showOnboarding && !isPublicRoute) {
    return <LoadingSpinner />
  } 

  return children
}